import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

// 微信登录
export const wechatLogin = createAsyncThunk(
  'auth/wechatLogin',
  async (code, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/wechat-login', { code });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '登录失败，请稍后重试'
      );
    }
  }
);

// 邮箱注册
export const emailRegister = createAsyncThunk(
  'auth/emailRegister',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '注册失败，请稍后重试'
      );
    }
  }
);

// 邮箱登录
export const emailLogin = createAsyncThunk(
  'auth/emailLogin',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '登录失败，请稍后重试'
      );
    }
  }
);

// 获取用户信息
export const getUserInfo = createAsyncThunk(
  'auth/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/user-info');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '获取用户信息失败'
      );
    }
  }
);

// 收藏单词
export const favoriteWord = createAsyncThunk(
  'auth/favoriteWord',
  async (wordData, { rejectWithValue }) => {
    try {
      const response = await api.post('/favorites/add', wordData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '收藏失败，请稍后重试'
      );
    }
  }
);

// 取消收藏单词
export const unfavoriteWord = createAsyncThunk(
  'auth/unfavoriteWord',
  async (wordId, { rejectWithValue }) => {
    try {
      await api.delete(`/favorites/${wordId}`);
      return wordId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '取消收藏失败，请稍后重试'
      );
    }
  }
);

// 获取收藏列表
export const getFavorites = createAsyncThunk(
  'auth/getFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/favorites');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || '获取收藏列表失败'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    showLoginModal: false,
    showRegisterModal: false,
    showFavoritesModal: false,
    isLoginMode: true, // true: 登录模式, false: 注册模式
    favorites: [],
    favoriteWords: [] // 改用数组来避免Set的序列化问题
  },
  reducers: {
    setShowLoginModal: (state, action) => {
      state.showLoginModal = action.payload;
      if (action.payload) {
        state.isLoginMode = true; // 打开登录框时默认为登录模式
      }
    },
    setShowRegisterModal: (state, action) => {
      state.showRegisterModal = action.payload;
    },
    setShowFavoritesModal: (state, action) => {
      state.showFavoritesModal = action.payload;
    },
    setLoginMode: (state, action) => {
      state.isLoginMode = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.favorites = [];
      state.favoriteWords = [];
      localStorage.removeItem('token');
    },
    clearError: (state) => {
      state.error = null;
    },
    checkAuthStatus: (state) => {
      const token = localStorage.getItem('token');
      if (token) {
        state.isAuthenticated = true;
        // 如果有token但用户信息为空，可以尝试获取用户信息
        if (!state.user) {
          // 这里不直接调用，而是通过组件来调用getUserInfo
        }
      }
    },
    initializeAuth: (state) => {
      const token = localStorage.getItem('token');
      if (token) {
        state.isAuthenticated = true;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // 微信登录
      .addCase(wechatLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(wechatLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.showLoginModal = false;
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
      })
      .addCase(wechatLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showLoginModal = false;
      })
      // 获取用户信息
      .addCase(getUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        localStorage.removeItem('token');
      })
      // 收藏单词
      .addCase(favoriteWord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(favoriteWord.fulfilled, (state, action) => {
        state.loading = false;
        const newFavorite = action.payload.data;
        state.favorites.unshift(newFavorite);
        if (!state.favoriteWords.includes(newFavorite.word)) {
          state.favoriteWords.push(newFavorite.word);
        }
      })
      .addCase(favoriteWord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 取消收藏单词
      .addCase(unfavoriteWord.fulfilled, (state, action) => {
        const wordId = action.payload;
        // 找到要删除的收藏项，获取其单词
        const favoriteToDelete = state.favorites.find(fav => fav.id === wordId);
        
        if (favoriteToDelete) {
          state.favorites = state.favorites.filter(fav => fav.id !== wordId);
          state.favoriteWords = state.favoriteWords.filter(word => word !== favoriteToDelete.word);
        }
      })
      // 获取收藏列表
      .addCase(getFavorites.fulfilled, (state, action) => {
        state.favorites = action.payload.data;
        state.favoriteWords = action.payload.data.map(fav => fav.word);
      })
      // 邮箱注册
      .addCase(emailRegister.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(emailRegister.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.showRegisterModal = false;
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
      })
      .addCase(emailRegister.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 邮箱登录
      .addCase(emailLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(emailLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.showLoginModal = false;
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
      })
      .addCase(emailLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  setShowLoginModal, 
  setShowRegisterModal,
  setShowFavoritesModal,
  setLoginMode,
  logout, 
  clearError, 
  checkAuthStatus,
  initializeAuth
} = authSlice.actions;

export default authSlice.reducer;